import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, ExternalLink, ClipboardList } from "lucide-react";

export default function BolViewerDialog({ open, onOpenChange, quote, initialBlobUrl }) {
  const [url, setUrl] = useState(null);
  const [ownUrl, setOwnUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !quote) { setUrl(null); return; }
    if (initialBlobUrl) { setUrl(initialBlobUrl); return; }
    setLoading(true);
    api.get(`/quotes/${quote.id}/bol/pdf`, { responseType: "blob" })
      .then((res) => {
        const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setUrl(u);
        setOwnUrl(u);
      })
      .catch(async (e) => {
        let msg = apiError(e);
        try {
          const t = await e?.response?.data?.text?.();
          if (t) { const j = JSON.parse(t); if (j.detail) msg = j.detail; }
        } catch { /* ignore */ }
        toast.error(msg);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBlobUrl, quote?.id]);

  useEffect(() => () => { if (ownUrl) window.URL.revokeObjectURL(ownUrl); }, [ownUrl]);

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${quote?.quote_number || "quote"}_RL_BOL_${quote?.bol_pro_number || ""}.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl gap-0 overflow-hidden p-0" data-testid="bol-viewer">
        <DialogDescription className="sr-only">Bill of Lading PDF preview{quote?.bol_pro_number ? ` for pro ${quote.bol_pro_number}` : ""}.</DialogDescription>
        <DialogHeader className="flex-row items-center justify-between space-y-0 border-b border-border px-4 py-3 pr-12">
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            <ClipboardList className="h-5 w-5 text-[hsl(var(--primary))]" /> Bill of Lading
            {quote?.bol_pro_number && (
              <span className="rounded bg-[hsl(var(--success))]/15 px-1.5 py-0.5 text-xs font-bold text-[hsl(var(--success))]">{quote.bol_pro_number}</span>
            )}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={download} disabled={!url} data-testid="bol-viewer-download">
              <Download className="mr-1.5 h-4 w-4" /> Download
            </Button>
            <Button variant="outline" size="sm" onClick={() => url && window.open(url, "_blank", "noopener")} disabled={!url} data-testid="bol-viewer-newtab">
              <ExternalLink className="mr-1.5 h-4 w-4" /> New Tab
            </Button>
          </div>
        </DialogHeader>
        <div className="h-[78vh] w-full bg-secondary/30">
          {loading || !url ? (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading BOL…
            </div>
          ) : (
            <iframe title="Bill of Lading" src={url} className="h-full w-full border-0" data-testid="bol-viewer-frame" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
